import { IColumn, Text } from '@fluentui/react'
import { CostBasis, SlowestAnswerItem } from '../../api/models'
import { formatDateTime } from '../../utils/formatDateTime'
import ConversationLinkCell from './ConversationLinkCell'
import OutcomeStatus from './OutcomeStatus'
import MessageText from './MessageText'
import { formatEmail, formatEstimatedCost, formatMilliseconds, formatPersonName, formatSeconds } from './performanceFormat'

export type AnswerRow = Pick<
  SlowestAnswerItem,
  | 'created_at'
  | 'total_ms'
  | 'cost'
  | 'status'
  | 'user_first_name'
  | 'user_last_name'
  | 'user_email'
  | 'plan_name'
  | 'prep_ms'
  | 'search_ms'
  | 'rerank_ms'
  | 'context_ms'
  | 'answer_ms'
  | 'message'
  | 'conversation_id'
>

type AnswerColumnOptions = {
  costBasis: CostBasis
  onOpenConversation: (conversationId: string) => void
}

const STAGE_FIELDS = [
  { field: 'prep_ms', label: 'Prep' },
  { field: 'search_ms', label: 'Search' },
  { field: 'rerank_ms', label: 'Rerank' },
  { field: 'context_ms', label: 'Context' },
  { field: 'answer_ms', label: 'Answer' },
] as const

const MISSING_RATE_NOTE = "No rate for one of this answer's models"

const VISUALLY_HIDDEN = {
  position: 'absolute' as const,
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap' as const,
}

const CostCell = ({ cost, costBasis }: { cost: number | null; costBasis: CostBasis }) => {
  const formatted = formatEstimatedCost(cost, costBasis.currency, costBasis.is_estimate)
  if (cost !== null) return <Text styles={{ root: { fontVariantNumeric: 'tabular-nums' } }}>{formatted}</Text>
  return (
    <Text title={MISSING_RATE_NOTE}>
      <span aria-hidden="true">{formatted}</span>
      <span style={VISUALLY_HIDDEN}>{MISSING_RATE_NOTE}</span>
    </Text>
  )
}

const describeStages = (row: AnswerRow): string =>
  STAGE_FIELDS.map(({ field, label }) => `${label} ${formatMilliseconds(row[field])}`).join(' · ')

export const buildAnswerColumns = ({
  costBasis,
  onOpenConversation,
}: AnswerColumnOptions): IColumn[] => [
  {
    key: 'created_at',
    name: 'Time',
    minWidth: 120,
    maxWidth: 150,
    isMultiline: true,
    onRender: (row: AnswerRow) => formatDateTime(row.created_at),
  },
  {
    key: 'total_ms',
    name: 'Total',
    minWidth: 55,
    maxWidth: 70,
    onRender: (row: AnswerRow) => (
      <Text styles={{ root: { fontWeight: 600, color: '#2B3A67' } }}>{formatSeconds(row.total_ms)}</Text>
    ),
  },
  {
    key: 'cost',
    name: 'Est. cost',
    minWidth: 70,
    maxWidth: 90,
    onRender: (row: AnswerRow) => <CostCell cost={row.cost} costBasis={costBasis} />,
  },
  {
    key: 'status',
    name: 'Status',
    minWidth: 100,
    maxWidth: 140,
    onRender: (row: AnswerRow) => <OutcomeStatus status={row.status} />,
  },
  {
    key: 'name',
    name: 'Name',
    minWidth: 100,
    maxWidth: 160,
    isMultiline: true,
    onRender: (row: AnswerRow) => formatPersonName(row.user_first_name, row.user_last_name),
  },
  {
    key: 'email',
    name: 'Email',
    minWidth: 140,
    maxWidth: 220,
    isMultiline: true,
    onRender: (row: AnswerRow) => formatEmail(row.user_email),
  },
  {
    key: 'plan_name',
    name: 'Plan',
    minWidth: 100,
    maxWidth: 180,
    isMultiline: true,
    onRender: (row: AnswerRow) => row.plan_name ?? '-',
  },
  {
    key: 'stages',
    name: 'Stages',
    minWidth: 180,
    isMultiline: true,
    onRender: (row: AnswerRow) => (
      <Text variant="small" styles={{ root: { color: '#605e5c', fontVariantNumeric: 'tabular-nums' } }}>
        {describeStages(row)}
      </Text>
    ),
  },
  {
    key: 'message',
    name: 'Message',
    minWidth: 240,
    isMultiline: true,
    onRender: (row: AnswerRow) => <MessageText message={row.message} />,
  },
  {
    key: 'conversation',
    name: 'Conversation',
    minWidth: 110,
    maxWidth: 130,
    onRender: (row: AnswerRow) => (
      <ConversationLinkCell conversationId={row.conversation_id} createdAt={row.created_at} onOpen={onOpenConversation} />
    ),
  },
]
